import { t } from "i18next";
import {
    CityRecord,
    getShopVatList,
    PostalCodeOption,
    resolveTvaFromList,
    Tranche,
} from "../shared";

export const INITIAL_CITY_AMOUNT_FORM_DATA = {
    codePostal: "",
    city: "",
    tva: "",
    country: "",
};

export const INITIAL_VALIDATION = {
    inputCode: false,
    inputCity: false,
    inputTva: false,
    selectCountry: false,
    isExistingCodePostal: false,
    isTranchesEmpty: false,
};

export function formatCityAmount(dataLivraison: CityRecord[]) {
    if (!dataLivraison || !Array.isArray(dataLivraison)) {
        return [];
    }

    let data: CityRecord[] = [];

    dataLivraison.forEach((element) => {
        const tranches = element.tiers || element.subrows || [];

        data.push({
            Department: element.Department,
            CodePostale: element.CodePostale,
            NomVille: element.NomVille,
            idTva: element.idTva,
            TauxTva: element.TauxTva,
            tiers: tranches,
            Pays: element.Pays,
            CodePays: element.CodePays,
            SousVille: element.SousVille,
        });
    });

    return data;
}

export function getCountryInfo(
    cityFormData: {
        codePostal: string;
        city: string;
        tva: string;
        country: string;
    },
    codeList: PostalCodeOption[]
) {
    const normalized = cityFormData.country.toLowerCase();
    const countryMap: Record<string, string> = {
        france: "France",
        tunisia: "Tunisie",
        tunisie: "Tunisie",
    };
    const countryLang = countryMap[normalized] || cityFormData.country;
    const codePays = codeList.find(
        (el) => countryLang.toLowerCase() === el.pays?.toLowerCase()
    )?.code;

    return { countryLang, codePays };
}

export function createDataVilleAmount(
    dataLivraison: CityRecord[],
    cityFormData: {
        codePostal: string;
        city: string;
        tva: string;
        country: string;
    },
    tranches: Tranche[],
    listcode: PostalCodeOption[],
    selectedRowShops: Record<string, unknown>[],
    franchiseID: string,
    isUpdate: boolean = false,
    selectedRowsUpdate: Record<string, unknown>[]
) {
    const tva = resolveTvaFromList(
        cityFormData.tva,
        getShopVatList(selectedRowShops)
    );
    const { countryLang, codePays } = getCountryInfo(cityFormData, listcode);

    // Auto-append unlimited last tier if missing
    let finalTranches = [...tranches];
    if (
        finalTranches.length > 0 &&
        finalTranches[finalTranches.length - 1].MaxCommande !== 0
    ) {
        const lastTier = finalTranches[finalTranches.length - 1];
        finalTranches.push({
            MinCommande: lastTier.MaxCommande,
            MaxCommande: 0,
            FraisLivraison: 0,
        });
    }

    let cityDetails: CityRecord = {
        Department: "",
        CodePostale: cityFormData.codePostal,
        NomVille: cityFormData.city,
        idTva: tva ? Number(tva.idTva) : 0,
        TauxTva: tva?.tauxTva ?? cityFormData.tva,
        tiers: finalTranches,
        Pays: countryLang,
        CodePays: codePays || "",
        SousVille: "",
    };

    if (isUpdate) {
        const postalCodeToUpdate =
            selectedRowsUpdate[0][t("Postal code")] ||
            selectedRowsUpdate[0]["Code Postal"];
        dataLivraison = dataLivraison.map((element) =>
            element.CodePostale === postalCodeToUpdate
                ? { ...element, ...cityDetails }
                : element
        );
    } else {
        dataLivraison.push(cityDetails);
    }
    dataLivraison = formatCityAmount(dataLivraison);
    let data = {
        shopId: selectedRowShops[0].shopId,
        franchiseID: franchiseID,
        key: "STVilleLivraisonMontant",
        data: dataLivraison,
    };
    return data;
}

export const validateAndSubmit = (
    cityFormData: {
        codePostal: string;
        city: string;
        tva: string;
        country: string;
    },
    validation: {
        inputCode: boolean;
        inputCity: boolean;
        inputTva: boolean;
        selectCountry: boolean;
        isExistingCodePostal: boolean;
        isTranchesEmpty: boolean;
    },
    updateValidation: (field: string, value: boolean) => void,
    tranches: Tranche[]
) => {
    const validationsMap: Record<string, string> = {
        codePostal: "inputCode",
        tva: "inputTva",
        country: "selectCountry",
        city: "inputCity",
    };
    Object.entries(validationsMap).forEach(([field, key]) => {
        if (!cityFormData[field as keyof typeof cityFormData]) {
            updateValidation(key, true);
        }
    });
    if (tranches.length === 0) {
        updateValidation("isTranchesEmpty", true);
    }
    const isFormValid =
        cityFormData.codePostal &&
        cityFormData.country &&
        cityFormData.tva &&
        cityFormData.city &&
        !validation.isExistingCodePostal &&
        tranches.length > 0;
    return isFormValid;
};
export function formatCityAmountLivraisonData(
    cityData: Record<string, unknown>[]
): CityRecord[] {
    return (cityData || []).map(
        (el): CityRecord => ({
            Department: String(el.Department || ""),
            CodePostale: String(
                el[t("Postal code")] || el["Code Postal"] || ""
            ),
            NomVille: String(el[t("City")] || el["Ville"] || ""),
            idTva: String(el.idTva || ""),
            TauxTva: String(el.Tva || ""),
            tiers: (el.subrows || el.tiers || []) as Tranche[],
            Pays: String(el.Pays !== undefined ? el.Pays : el.Country || ""),
            CodePays: String(el.CodePays || ""),
            SousVille: String(el.SousVille || ""),
        })
    );
}
